import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Card, Col, Container, Form, Row, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import Select from "../../components/ui/AppReactSelect";
import { toast } from "react-toastify";

import { createStaffProfile } from "../../actions/auth";
import {
  apiRbacReplacePermissionOverrides,
  apiCheckStaffEmail,
  apiResendStaffInvite,
  apiPromoteClientToStaff,
} from "../../api/index";
import StaffFeatureAccessEditor from "../../components/StaffFeatureAccessEditor";
import { getUserFromLocalStorage } from "utilities/user";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ROLE_OPTIONS = [
  { value: "SUPERUSER",  label: "Super User",  description: "Full platform access" },
  { value: "ADMIN",      label: "Admin",        description: "Administrative access" },
  { value: "SUPERVISOR", label: "Supervisor",   description: "Supervisory access" },
  { value: "CREATOR",    label: "Creator",      description: "Content creation access" },
  { value: "CONSUMER",   label: "Consumer",     description: "Read-only access" },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// How long after the last keystroke the address is checked. Long enough not to
// fire mid-word, short enough that the answer is there before Create is pressed.
const EMAIL_CHECK_DELAY_MS = 450;

// ---------------------------------------------------------------------------
// Inline field error
// ---------------------------------------------------------------------------

const FieldError = ({ msg }) =>
  msg ? <div style={{ color: "#dc2626", fontSize: 12, marginTop: 4 }}>{msg}</div> : null;

// ---------------------------------------------------------------------------
// Notices under the email field
// ---------------------------------------------------------------------------
//
// Creating a staff account used to fail with a red "User already Exists" toast
// and nothing else — no name, no account type, no id, no next step. An admin
// could not tell whether the person was already staff, was a shopper who had
// once used their work address, or had been deactivated last month; retyping
// the address produced the same sentence. The server now answers a clash with
// the account it found and the actions that resolve it, and this renders both
// in place, under the field that caused it.

const NOTICE_TONE = {
  blocked: { bg: "#fef2f2", border: "#fecaca", fg: "#7f1d1d", icon: "fa-exclamation-circle" },
  warn:    { bg: "#fffbeb", border: "#fde68a", fg: "#78350f", icon: "fa-exclamation-triangle" },
  info:    { bg: "#f0f9ff", border: "#bae6fd", fg: "#0c4a6e", icon: "fa-envelope" },
};

const Notice = ({ tone = "info", children }) => {
  const t = NOTICE_TONE[tone] || NOTICE_TONE.info;
  return (
    <div
      style={{
        display: "flex", alignItems: "flex-start", gap: 10,
        background: t.bg, border: `1px solid ${t.border}`,
        borderRadius: 8, padding: "12px 14px", marginBottom: 20,
      }}
    >
      <i className={`fas ${t.icon}`} style={{ color: t.fg, marginTop: 2, opacity: 0.85 }} />
      <div style={{ fontSize: 13, color: t.fg, lineHeight: 1.5, flex: 1 }}>{children}</div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

// Passwordless onboarding: no password fields — the new staff member receives
// an email with their sign-in address and a 48h link to set their own password.
const EMPTY_FORM = {
  firstName: "",
  lastName: "",
  email: "",
  user_role: "",
};

const EMPTY_ERRORS = {
  firstName: "",
  lastName: "",
  email: "",
  user_role: "",
};

const UserForm = () => {
  const user     = getUserFromLocalStorage();
  const dispatch = useDispatch();
  const history  = useHistory();

  const isLoading = useSelector((state) => state.auth?.isLoading || false);

  const [postData, setPostData]       = useState(EMPTY_FORM);
  const [errors, setErrors]           = useState(EMPTY_ERRORS);
  const [submitting, setSubmitting]   = useState(false);
  const [permissionOverrides, setPermissionOverrides] = useState([]);
  const [accessReady, setAccessReady] = useState(false);
  // The server's verdict on the typed address: { available, code, message,
  // actions, existing }. Null means "nothing to say".
  const [conflict, setConflict]       = useState(null);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [resolving, setResolving]     = useState(false);
  const canCustomizeAccess = user?.result?.user_role === "SUPERUSER";
  const actorId = user?.result?._id;
  const handleAccessChange = useCallback(
    (overrides) => setPermissionOverrides(overrides),
    []
  );

  // A late reply about an address the admin has since edited must not overwrite
  // a newer answer, so every check carries a sequence number and only the
  // latest is allowed to land.
  const checkSeq = useRef(0);

  // ── Live availability check ───────────────────────────────────────────────
  const typedEmail = postData.email.trim().toLowerCase();
  useEffect(() => {
    if (!actorId || !EMAIL_RE.test(typedEmail)) {
      setConflict(null);
      setCheckingEmail(false);
      return undefined;
    }
    const seq = ++checkSeq.current;
    setCheckingEmail(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await apiCheckStaffEmail(actorId, typedEmail);
        if (seq !== checkSeq.current) return;
        setConflict(data && data.code === "AVAILABLE" ? null : data);
      } catch (checkErr) {
        // A failed pre-flight is not the admin's problem to solve: Create runs
        // the same check server-side, so staying quiet here costs nothing.
        console.debug("[UserForm] email check failed:", checkErr?.message);
        if (seq === checkSeq.current) setConflict(null);
      } finally {
        if (seq === checkSeq.current) setCheckingEmail(false);
      }
    }, EMAIL_CHECK_DELAY_MS);
    return () => clearTimeout(timer);
  }, [typedEmail, actorId]);

  // ── Field change ──────────────────────────────────────────────────────────
  const handleChange = (field, value) => {
    setPostData((prev) => ({ ...prev, [field]: value }));
    // Clear the error for this field on change
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  // ── Validation ────────────────────────────────────────────────────────────
  const validate = () => {
    const errs = { ...EMPTY_ERRORS };
    let valid = true;

    if (!postData.firstName.trim()) {
      errs.firstName = "First name is required";
      valid = false;
    }
    if (!postData.lastName.trim()) {
      errs.lastName = "Last name is required";
      valid = false;
    }
    if (!postData.email.trim()) {
      errs.email = "Email address is required";
      valid = false;
    } else if (!EMAIL_RE.test(postData.email.trim())) {
      errs.email = "Enter a valid email address";
      valid = false;
    }
    if (!postData.user_role) {
      errs.user_role = "Please select a role";
      valid = false;
    }

    setErrors(errs);
    return valid;
  };

  // ── Conflict actions ──────────────────────────────────────────────────────
  const existingId = conflict?.existing?.id;
  const can = (action) =>
    Array.isArray(conflict?.actions) && conflict.actions.includes(action);

  const handleResendInvite = async () => {
    if (!existingId || !actorId) return;
    setResolving(true);
    try {
      const { data } = await apiResendStaffInvite(actorId, existingId);
      toast.success(data?.message || "Invitation sent.");
      history.push("/admin/users");
    } catch (err) {
      toast.error(err?.response?.data?.message || "The invitation could not be sent.");
    } finally {
      setResolving(false);
    }
  };

  const handlePromote = async () => {
    if (!existingId || !actorId) return;
    if (!postData.user_role) {
      setErrors((prev) => ({ ...prev, user_role: "Choose the role they should have" }));
      return;
    }
    setResolving(true);
    try {
      const { data } = await apiPromoteClientToStaff(
        actorId,
        existingId,
        postData.user_role
      );
      toast.success(data?.message || "Staff access granted.");
      if (data?.inviteEmailSent === false && data?.inviteEmailIssue) {
        toast.warn(data.inviteEmailIssue, { autoClose: 9000 });
      }
      history.push("/admin/users");
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "That account could not be given staff access."
      );
    } finally {
      setResolving(false);
    }
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      const created = await dispatch(
        createStaffProfile(
          {
            firstName: postData.firstName.trim(),
            lastName:  postData.lastName.trim(),
            email:     postData.email.trim().toLowerCase(),
            user_role: postData.user_role,
            // No password — the backend stores a random placeholder and emails
            // the new staff member their sign-in address plus an invite link
            // (48h) to set their own. The link's origin is resolved server-side;
            // the client deliberately does not get to choose it.
          },
          user?.result?._id
        )
      );
      const createdUserId = created?.result?._id;
      if (
        canCustomizeAccess &&
        accessReady &&
        permissionOverrides.length &&
        createdUserId
      ) {
        try {
          await apiRbacReplacePermissionOverrides(
            createdUserId,
            permissionOverrides,
            "Configured during staff account creation"
          );
        } catch (accessError) {
          toast.error(
            accessError?.response?.data?.message ||
              "The account was created, but its custom feature access was not saved."
          );
          history.push(`/admin/user-edit/${createdUserId}`);
          return;
        }
      }
      history.push("/admin/users");
    } catch (err) {
      // 409 — the address belongs to somebody. Show who, and what can be done
      // about it, next to the field that caused it. The action has already
      // toasted every other kind of failure, so there is nothing to add here;
      // toasting again is what put "User already Exists" on screen twice.
      if (err?.response?.status === 409 && err?.response?.data) {
        setConflict(err.response.data);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const isBusy = submitting || isLoading || resolving;
  // REACTIVATABLE is a heads-up, not a block: creating really does reactivate
  // that account, which is what the admin wants — they should just know that is
  // what is about to happen.
  const isBlocked = Boolean(conflict) && conflict.available === false;
  const pendingInvite = conflict?.existing?.pendingInvite;

  // ── Selected role option (for controlled Select) ──────────────────────────
  const selectedRole = ROLE_OPTIONS.find((o) => o.value === postData.user_role) || null;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Container fluid>
      <Row>
        <Col md="12" lg="11">
          <Card className="stacked-form" style={{ borderRadius: 12, boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}>
            <Card.Header style={{ borderBottom: "1px solid #f1f5f9", paddingBottom: 14 }}>
              <Card.Title as="h4" style={{ margin: 0, color: "#0f172a", fontWeight: 700 }}>
                Create Staff Account
              </Card.Title>
              <p style={{ margin: "4px 0 0", fontSize: 13, color: "#64748b" }}>
                New Lusuku staff member with platform access
              </p>
            </Card.Header>

            <Card.Body style={{ paddingTop: 24 }}>
              <Form autoComplete="off" onSubmit={handleSubmit} noValidate>

                {/* ── Name row ── */}
                <Row>
                  <Col md="6">
                    <Form.Group style={{ marginBottom: 16 }}>
                      <Form.Label style={{ fontWeight: 600, fontSize: 13, color: "#475569" }}>
                        First Name <span style={{ color: "#dc2626" }}>*</span>
                      </Form.Label>
                      <Form.Control
                        placeholder="First name"
                        name="firstName"
                        type="text"
                        value={postData.firstName}
                        onChange={(e) => handleChange("firstName", e.target.value)}
                        isInvalid={!!errors.firstName}
                        style={{ borderRadius: 8 }}
                      />
                      <FieldError msg={errors.firstName} />
                    </Form.Group>
                  </Col>
                  <Col md="6">
                    <Form.Group style={{ marginBottom: 16 }}>
                      <Form.Label style={{ fontWeight: 600, fontSize: 13, color: "#475569" }}>
                        Last Name <span style={{ color: "#dc2626" }}>*</span>
                      </Form.Label>
                      <Form.Control
                        placeholder="Last name"
                        name="lastName"
                        type="text"
                        value={postData.lastName}
                        onChange={(e) => handleChange("lastName", e.target.value)}
                        isInvalid={!!errors.lastName}
                        style={{ borderRadius: 8 }}
                      />
                      <FieldError msg={errors.lastName} />
                    </Form.Group>
                  </Col>
                </Row>

                {/* ── Email ── */}
                <Form.Group style={{ marginBottom: 16 }}>
                  <Form.Label style={{ fontWeight: 600, fontSize: 13, color: "#475569" }}>
                    Email Address <span style={{ color: "#dc2626" }}>*</span>
                    {checkingEmail && (
                      <span style={{ fontWeight: 400, fontSize: 12, color: "#94a3b8", marginLeft: 8 }}>
                        checking…
                      </span>
                    )}
                  </Form.Label>
                  <Form.Control
                    placeholder="staff@lusuku.ug"
                    name="email"
                    type="email"
                    value={postData.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    isInvalid={!!errors.email || isBlocked}
                    style={{ borderRadius: 8 }}
                  />
                  <FieldError msg={errors.email} />
                </Form.Group>

                {/* ── Who holds this address, or what create will do ── */}
                {isBlocked ? (
                  <Notice tone="blocked">
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>
                      This email is already in use
                    </div>
                    <div>{conflict.message}</div>
                    {pendingInvite?.expiresAt && (
                      <div style={{ marginTop: 6, fontSize: 12, opacity: 0.85 }}>
                        Their set-password link{" "}
                        {pendingInvite.expired ? "expired" : "expires"}{" "}
                        {new Date(pendingInvite.expiresAt).toLocaleString()}
                      </div>
                    )}
                    {(can("RESEND_INVITE") || can("PROMOTE_TO_STAFF") || can("OPEN_ACCOUNT")) && (
                      <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                        {can("RESEND_INVITE") && (
                          <Button
                            size="sm"
                            variant="info"
                            type="button"
                            disabled={isBusy}
                            onClick={handleResendInvite}
                            style={{ borderRadius: 6, fontWeight: 600 }}
                          >
                            Resend their invitation
                          </Button>
                        )}
                        {can("PROMOTE_TO_STAFF") && (
                          <Button
                            size="sm"
                            variant="success"
                            type="button"
                            disabled={isBusy}
                            onClick={handlePromote}
                            style={{ borderRadius: 6, fontWeight: 600 }}
                          >
                            Give this account staff access
                          </Button>
                        )}
                        {can("OPEN_ACCOUNT") && existingId && (
                          <Button
                            size="sm"
                            variant="light"
                            type="button"
                            disabled={isBusy}
                            onClick={() => history.push(`/admin/user-edit/${existingId}`)}
                            style={{ borderRadius: 6, fontWeight: 600, border: "1px solid #e2e8f0" }}
                          >
                            Open the account
                          </Button>
                        )}
                      </div>
                    )}
                  </Notice>
                ) : conflict?.code === "REACTIVATABLE" ? (
                  <Notice tone="warn">{conflict.message}</Notice>
                ) : (
                  <Notice tone="info">
                    No password needed — we'll email{" "}
                    <strong>{postData.email.trim() || "the new staff member"}</strong> their
                    sign-in details and a secure link (valid 48 hours) so they set their own
                    password.
                  </Notice>
                )}

                {/* ── Assign Role ── */}
                <div style={{ borderTop: "1px solid #f1f5f9", paddingTop: 20, marginBottom: 20 }}>
                  <Form.Label style={{ fontWeight: 700, fontSize: 14, color: "#0f172a", display: "block", marginBottom: 8 }}>
                    Assign Role <span style={{ color: "#dc2626" }}>*</span>
                  </Form.Label>
                  <Select
                    className="react-select primary"
                    classNamePrefix="react-select"
                    name="user_role"
                    value={selectedRole}
                    onChange={(opt) => {
                      handleChange("user_role", opt ? opt.value : "");
                      setPermissionOverrides([]);
                    }}
                    options={ROLE_OPTIONS}
                    placeholder="Select a role..."
                    formatOptionLabel={(opt) => (
                      <div>
                        <span style={{ fontWeight: 600, fontSize: 13 }}>{opt.label}</span>
                        <span style={{ fontSize: 11, color: "#94a3b8", marginLeft: 8 }}>{opt.description}</span>
                      </div>
                    )}
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        borderRadius: 8,
                        borderColor: errors.user_role ? "#dc2626" : state.isFocused ? "#3b82f6" : "#e2e8f0",
                        boxShadow: "none",
                        "&:hover": { borderColor: "#3b82f6" },
                      }),
                    }}
                  />
                  <FieldError msg={errors.user_role} />
                </div>

                <StaffFeatureAccessEditor
                  role={postData.user_role}
                  canCustomize={canCustomizeAccess}
                  onChange={handleAccessChange}
                  onReadyChange={setAccessReady}
                />

                {/* ── Submit ── */}
                <div style={{ display: "flex", gap: 10, marginTop: 22 }}>
                  <Button
                    variant="info"
                    type="submit"
                    disabled={isBusy || isBlocked}
                    style={{ borderRadius: 8, fontWeight: 600, minWidth: 130, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
                  >
                    {isBusy ? (
                      <>
                        <Spinner animation="border" size="sm" />
                        Creating...
                      </>
                    ) : (
                      "Create Account"
                    )}
                  </Button>
                  <Button
                    variant="light"
                    type="button"
                    onClick={() => history.push("/admin/users")}
                    disabled={isBusy}
                    style={{ borderRadius: 8, fontWeight: 600, border: "1px solid #e2e8f0" }}
                  >
                    Cancel
                  </Button>
                </div>

              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default UserForm;
